/**
 * YMQ multi-select -> quantity sync (product-scoped, not global).
 *
 * Activates ONLY when this YMQ multi-select dropdown exists on the page:
 *   .ymq-app-select-main.ymq-dropdown.ymq-option-show-checkbox
 *
 * NOTE: `ymq-app-select-open-below` is NOT part of the selector on purpose —
 * YMQ adds it only while the dropdown is open and removes it on close, so
 * requiring it would break detection most of the time.
 *
 * Behaviour:
 *   quantity = max(1, ceil(selected labels / labels-per-unit))
 *   - "Solo" variant (1 label per unit): 1 label selected = qty 1.
 *   - "Set of 2" variant (2 labels per unit): 2 labels selected = qty 1.
 *   - Labels-per-unit follows the selected variant (VARIANT_DIVISOR below)
 *     and re-syncs on every variant change.
 *   - Selecting an option increases the Dawn quantity input.
 *   - Deselecting decreases it again (floored at 1).
 *   - The value is re-synced on form submit (capture phase), so the
 *     quantity sent to /cart/add always matches the selection — no extra
 *     cart logic needed because product-form.js reads the same input.
 *
 * Loaded from snippets/buy-buttons.liquid (product form context only),
 * never from theme.liquid, and self-gates when the component is absent.
 */
(function () {
  if (window.__ymqMultiQtyInit) return;
  window.__ymqMultiQtyInit = true;

  // Stable classes only: `ymq-app-select-open-below` is added on open and
  // removed on close by YMQ, so it must NOT be required here.
  var MAIN_SELECTOR = '.ymq-app-select-main.ymq-dropdown.ymq-option-show-checkbox';
  var VALUE_PILL_SELECTOR = '.ymq-app-select-value';
  var OPTION_SELECTED_CLASS = 'ymq-app-select-selected';

  // Set to true to log sync activity to the console (useful while testing).
  // TEMP: true while diagnosing the Set-of-2 divisor — set back to false after.
  var DEBUG = true;

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[ymq-qty-sync]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // When true, shoppers cannot manually type/click a different quantity while
  // the sync is active — qty is derived from the selection. Set to false to
  // allow manual edits (next YMQ change will still overwrite).
  var LOCK_QTY = true;

  function norm(s) {
    return String(s == null ? '' : s).trim().toLowerCase();
  }

  // How many selected YMQ labels make one unit of quantity, by variant.
  // Key = variant option value as shown in the picker ("Solo", "Set of 2").
  // Matching is case-insensitive. Variants not listed use DEFAULT_DIVISOR.
  var VARIANT_DIVISOR = {
    solo: 1,
    'set of 2': 2,
  };
  var DEFAULT_DIVISOR = 1;

  // Read the currently selected variant option values ("Solo" / "Set of 2")
  // from the theme's own variant picker: checked radios, dropdown selects,
  // or the embedded selected-variant JSON as fallback.
  function selectedVariantValues(scope) {
    var values = [];
    var roots = [];
    if (scope && scope.querySelectorAll) roots.push(scope);
    if (scope !== document) roots.push(document);

    var i, j;
    for (i = 0; i < roots.length; i++) {
      var root = roots[i];
      var radios = root.querySelectorAll('variant-selects fieldset input[type="radio"]:checked');
      for (j = 0; j < radios.length; j++) {
        if (radios[j].value) values.push(radios[j].value);
      }
      var selects = root.querySelectorAll('variant-selects select');
      for (j = 0; j < selects.length; j++) {
        var sel = selects[j];
        var idx = sel.selectedIndex;
        if (idx >= 0 && sel.options[idx] && sel.options[idx].value) {
          values.push(sel.options[idx].value);
        }
      }
      if (values.length) return values;
      // Fallback: embedded JSON of the selected variant (option1..option3).
      var jsonEl = root.querySelector('script[data-selected-variant]');
      if (jsonEl) {
        try {
          var v = JSON.parse(jsonEl.textContent);
          ['option1', 'option2', 'option3'].forEach(function (k) {
            if (v && v[k]) values.push(v[k]);
          });
        } catch (e) {}
      }
      if (values.length) return values;
    }
    return values;
  }

  function labelsPerQty(scope) {
    var values = selectedVariantValues(scope);
    // 1. Exact match (case-insensitive).
    var best = 0;
    var i, key;
    for (i = 0; i < values.length; i++) {
      key = norm(values[i]);
      if (Object.prototype.hasOwnProperty.call(VARIANT_DIVISOR, key)) {
        best = Math.max(best, VARIANT_DIVISOR[key]);
      }
    }
    // 2. Substring match (longest key first): covers labels like
    // "Set of 2 Decanters" when the map key is "set of 2".
    if (!best) {
      var keys = Object.keys(VARIANT_DIVISOR).sort(function (a, b) {
        return b.length - a.length;
      });
      for (i = 0; i < values.length && !best; i++) {
        var nv = norm(values[i]);
        for (var k = 0; k < keys.length; k++) {
          if (nv.indexOf(keys[k]) !== -1) {
            best = VARIANT_DIVISOR[keys[k]];
            break;
          }
        }
      }
    }
    // 3. Auto-detect "set of N" in the variant label itself.
    if (!best) {
      for (i = 0; i < values.length && !best; i++) {
        var m = /set of\s+(\d+)/i.exec(values[i] || '');
        if (m) best = Math.max(1, parseInt(m[1], 10));
      }
    }
    return { divisor: best > 0 ? best : DEFAULT_DIVISOR, values: values };
  }

  function findMainElements() {
    return Array.prototype.slice.call(document.querySelectorAll(MAIN_SELECTOR));
  }

  function findScope(mainEl) {
    return (
      mainEl.closest('product-info') ||
      mainEl.closest('.product__info-container') ||
      mainEl.closest('main') ||
      document
    );
  }

  function findQuantityInput(scope) {
    // Prefer the product-form quantity input in the same product scope.
    var input = scope.querySelector('.quantity__input');
    if (input) return input;
    return document.querySelector('.quantity__input');
  }

  function findHiddenSelect(mainEl) {
    // YMQ inserts its custom UI directly after the native <select>.
    var sib = mainEl.previousElementSibling;
    while (sib) {
      if (sib.tagName === 'SELECT') return sib;
      sib = sib.previousElementSibling;
    }
    var parent = mainEl.parentElement;
    if (parent) {
      var sel = parent.querySelector('select[multiple], select');
      if (sel) return sel;
    }
    return null;
  }

  function selectedLabels(mainEl) {
    var select = findHiddenSelect(mainEl);
    var items = [];
    var i;

    if (select && select.options && select.options.length) {
      for (i = 0; i < select.options.length; i++) {
        var opt = select.options[i];
        if (opt.selected && opt.value !== '' && !opt.dataset.placeholder) {
          items.push({ value: opt.value, text: opt.text });
        }
      }
      // A hidden select with real options is the source of truth.
      if (items.length || select.options.length) return items;
    }

    // Fallbacks: use rendered text (shown in DEBUG logs).
    var pills = mainEl.querySelectorAll(VALUE_PILL_SELECTOR);
    if (pills.length) {
      for (i = 0; i < pills.length; i++) {
        items.push({ value: '', text: pills[i].textContent || '' });
      }
      return items;
    }

    // Fallback 2: selected option rows in the dropdown content panel.
    // YMQ appends the content panel to body with the same data-id.
    var id = mainEl.dataset && mainEl.dataset.id;
    if (id) {
      var content = document.querySelector(
        '.ymq-app-select-content[data-id="' + id + '"]'
      );
      if (content) {
        var selected = content.querySelectorAll('.' + OPTION_SELECTED_CLASS);
        for (i = 0; i < selected.length; i++) {
          items.push({ value: '', text: selected[i].textContent || '' });
        }
      }
    }

    return items;
  }

  function totalSelectedCount() {
    var mains = findMainElements();
    var items = [];
    for (var i = 0; i < mains.length; i++) {
      items = items.concat(selectedLabels(mains[i]));
    }
    // N labels make one unit, driven by the selected variant:
    // Solo divisor 1 (1 label = qty 1), Set-of-2 divisor 2 (2 labels = qty 1).
    // Always at least 1.
    var scope = mains.length ? findScope(mains[0]) : document;
    var per = labelsPerQty(scope);
    var qty = Math.max(1, Math.ceil(items.length / per.divisor));
    return {
      mains: mains,
      total: qty,
      labelCount: items.length,
      divisor: per.divisor,
      variantValues: per.values,
      items: items,
    };
  }

  function setQuantity(input, qty) {
    qty = Math.max(1, qty);

    // Respect variant quantity rules when present.
    var min = parseInt(input.getAttribute('min') || input.dataset.min || '1', 10);
    var maxAttr = input.getAttribute('max') || input.dataset.max;
    if (!isNaN(min)) qty = Math.max(min, qty);
    if (maxAttr !== null && maxAttr !== undefined && maxAttr !== '') {
      var max = parseInt(maxAttr, 10);
      if (!isNaN(max)) qty = Math.min(max, qty);
    }

    if (parseInt(input.value, 10) === qty) return false;

    input.value = qty;
    input.setAttribute('value', qty);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function sync(reason) {
    var result = totalSelectedCount();
    log(
      'sync (' +
        reason +
        '): mains=' +
        result.mains.length +
        ' variant=[' +
        result.variantValues.join(' | ') +
        '] divisor=' +
        result.divisor +
        ' labels=' +
        result.labelCount +
        ' qty=' +
        result.total
    );
    if (DEBUG) {
      for (var d = 0; d < result.items.length; d++) {
        log('  label value="' + result.items[d].value + '" text="' + result.items[d].text + '"');
      }
    }
    if (!result.mains.length) {
      applyLock(false);
      return;
    }
    var scope = findScope(result.mains[0]);
    var input = findQuantityInput(scope);
    if (!input) {
      log('no .quantity__input found in scope');
      return;
    }
    var changed = setQuantity(input, result.total);
    log('quantity input value=' + input.value + (changed ? ' (updated)' : ' (unchanged)'));
    applyLock(true);
  }

  function applyLock(active) {
    if (!LOCK_QTY) return;
    var result = totalSelectedCount();
    if (!result.mains.length) active = false;
    var scope = result.mains.length ? findScope(result.mains[0]) : document;
    var qtyInput = scope.querySelector && scope.querySelector('quantity-input');
    if (!qtyInput) qtyInput = document.querySelector('quantity-input');
    if (!qtyInput) return;

    var input = qtyInput.querySelector('input');
    var buttons = qtyInput.querySelectorAll('button');
    if (active) {
      if (input) input.setAttribute('readonly', 'readonly');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute('aria-disabled', 'true');
        buttons[i].classList.add('disabled');
      }
      qtyInput.setAttribute('data-ymq-qty-locked', 'true');
    } else {
      if (input) input.removeAttribute('readonly');
      for (var j = 0; j < buttons.length; j++) {
        buttons[j].removeAttribute('aria-disabled');
      }
      qtyInput.removeAttribute('data-ymq-qty-locked');
    }
  }

  var scheduled = false;
  function scheduleSync(delay) {
    if (scheduled) return;
    scheduled = true;
    // YMQ updates the hidden <select> asynchronously after a click.
    setTimeout(function () {
      scheduled = false;
      sync('async');
    }, delay || 120);
  }

  function init() {
    log('loaded, searching for ' + MAIN_SELECTOR);
    if (!findMainElements().length) {
      // YMQ renders client-side after page load — watch for it appearing.
      var bootObserver = new MutationObserver(function () {
        if (findMainElements().length) {
          bootObserver.disconnect();
          bind();
          sync('boot');
        }
      });
      bootObserver.observe(document.documentElement, { childList: true, subtree: true });
      // Stop watching after 20s to avoid a lingering observer on
      // products that never render this component.
      setTimeout(function () {
        bootObserver.disconnect();
      }, 20000);
      return;
    }
    bind();
    sync('init');
  }

  function bind() {
    // 1. Native change events from YMQ's hidden <select>.
    document.addEventListener(
      'change',
      function (event) {
        var t = event.target;
        if (t && t.tagName === 'SELECT') {
          var main = t.parentElement
            ? t.parentElement.querySelector(MAIN_SELECTOR)
            : null;
          if (main || t.closest('product-info')) scheduleSync();
          return;
        }
        if (t && t.classList && t.classList.contains('quantity__input')) {
          // Manual edit while locked — re-assert derived quantity.
          if (
            LOCK_QTY &&
            document.querySelector('quantity-input[data-ymq-qty-locked="true"]')
          ) {
            scheduleSync();
          }
          return;
        }
        if (t && t.closest && t.closest('variant-selects')) {
          // Variant picker interaction (radios): re-sync now for the new
          // divisor, and again after the theme re-renders product info.
          scheduleSync();
          setTimeout(function () {
            sync('variant');
          }, 800);
        }
      },
      true
    );

    // 2. Clicks on YMQ custom UI (options, pill delete, clear-all).
    document.addEventListener(
      'click',
      function (event) {
        var t = event.target;
        if (!t || !t.closest) return;
        if (
          t.closest('.ymq-app-select-option') ||
          t.closest('.ymq-app-select-value-delete') ||
          t.closest('.ymq-app-select-deselect') ||
          t.closest('.ymq-app-select-main')
        ) {
          scheduleSync();
        }
      },
      true
    );

    // 3. Guard: re-sync on add-to-cart submit (capture runs before
    // product-form.js / YMQ build their FormData), so cart qty always matches.
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.closest('cart-drawer')) return;
        if (!findMainElements().length) return;
        var scope =
          form.closest('product-info') ||
          (form.id && document.querySelector('[form="' + form.id + '"]')
            ? document
            : document);
        var input =
          scope.querySelector('.quantity__input') ||
          document.querySelector('.quantity__input');
        if (!input) return;
        var result = totalSelectedCount();
        setQuantity(input, result.total);
      },
      true
    );

    // 4. MutationObserver for YMQ late renders + selection class changes.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList') {
          var added = m.addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            // Theme re-rendered the quantity input (e.g. after variant
            // change) — the replacement loses our value and lock.
            if (
              (node.matches && node.matches('.quantity__input')) ||
              (node.querySelector && node.querySelector('.quantity__input'))
            ) {
              scheduleSync(400);
              return;
            }
            if (
              (node.matches && node.matches(MAIN_SELECTOR)) ||
              (node.querySelector && node.querySelector(MAIN_SELECTOR)) ||
              (node.matches &&
                (node.matches('.ymq-app-select-value') ||
                  node.matches('.ymq-app-select-content')))
            ) {
              scheduleSync();
              return;
            }
          }
          var removed = m.removedNodes;
          for (var k = 0; k < removed.length; k++) {
            var rnode = removed[k];
            if (rnode.nodeType !== 1) continue;
            if (
              (rnode.matches && rnode.matches('.ymq-app-select-value')) ||
              (rnode.querySelector && rnode.querySelector('.ymq-app-select-value'))
            ) {
              scheduleSync();
              return;
            }
          }
        }
        if (
          m.type === 'attributes' &&
          m.attributeName === 'class' &&
          m.target &&
          m.target.classList &&
          (m.target.classList.contains('ymq-app-select-option') ||
            m.target.classList.contains('ymq-app-select-value'))
        ) {
          scheduleSync();
          return;
        }
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // 5. Theme variant-change event (published by product-info after the new
    // variant resolves). Re-sync now and after the section re-render lands.
    try {
      if (typeof subscribe === 'function') {
        subscribe('variant-change', function () {
          if (!findMainElements().length) return;
          scheduleSync();
          setTimeout(function () {
            sync('variant-change');
          }, 800);
        });
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();